import React, { useState } from 'react';
import { EditPropertyModal } from '../EditPropertyModal';
import { CreatePropertyModal } from '../../../components/admin/CreatePropertyModal';
import { AdminPropertyManagement } from './AdminPropertyManagement';
import { HomeModernIcon, PlusIcon } from '../../../components/Icons';
import { AppUser, Property } from '../../../types';
import { searchByCodesApi } from '../../../services/propertyApiClient';
import { savePropertyApi, createPropertyApi } from '../../../services/adminPropertyApiClient';

interface SuperAdminPropertyManagementSectionProps {
  appUser: AppUser | null;
}

export const SuperAdminPropertyManagementSection: React.FC<SuperAdminPropertyManagementSectionProps> = () => {
  const [propertySearchCode, setPropertySearchCode] = useState('');
  const [foundProperty, setFoundProperty] = useState<Property | Property[] | null>(null);
  const [editingProperty, setEditingProperty] = useState<Property | null>(null);
  const [isLoadingSearch, setIsLoadingSearch] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const handleSearchProperty = async () => {
    if (!propertySearchCode.trim()) return;
    setIsLoadingSearch(true);
    setMessage(null);
    setFoundProperty(null);
    try {
      const res = await searchByCodesApi([propertySearchCode.trim()]);
      if (res.items && res.items.length > 0) {
        setFoundProperty(res.items.length === 1 ? res.items[0] : res.items);
        setMessage(`Inmueble(s) encontrado(s): ${res.items.length}`);
      } else {
        setMessage('No se encontró ningún inmueble con el código proporcionado.');
      }
    } catch (err: any) {
      setMessage(`Error al buscar inmueble: ${err?.message || 'Error desconocido'}`);
    } finally {
      setIsLoadingSearch(false);
    }
  };

  const handleSaveProperty = async (updatedData: Partial<Property>) => {
    if (!editingProperty?.id) return;
    await savePropertyApi(editingProperty.id, updatedData);
    setEditingProperty(null);
    setMessage(`Inmueble ${editingProperty.codigo || editingProperty.id} actualizado exitosamente.`);
    handleSearchProperty();
  };

  const handleCreateProperty = async (newProp: Partial<Property>) => {
    const res = await createPropertyApi(newProp, newProp.codigo);
    setIsCreateModalOpen(false);
    setMessage(`Inmueble creado con éxito (ID: ${res.id}).`);
    if (newProp.codigo) {
      setPropertySearchCode(newProp.codigo);
      const searchRes = await searchByCodesApi([newProp.codigo]);
      if (searchRes.items?.length) setFoundProperty(searchRes.items[0]);
    }
  };

  return (
    <section className="bg-white p-4 sm:p-6 shadow-lg rounded-2xl" id="super-admin-properties" role="tabpanel">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 border-b border-gray-100 pb-4">
        <div className="flex items-center text-sky-600">
          <HomeModernIcon className="w-8 h-8 mr-3 text-[#0073b7]" />
          <div>
            <h3 id="property-management-heading" className="text-xl font-bold text-gray-800">Gestión de Propiedades</h3>
            <p className="text-xs text-gray-500">Creación manual, búsqueda completa y edición integral de inmuebles.</p>
          </div>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="w-full sm:w-auto px-5 py-2.5 bg-[#0073b7] text-white rounded-xl text-xs font-black uppercase tracking-widest shadow-md hover:bg-sky-700 active:scale-95 transition-all flex items-center justify-center gap-2"
        >
          <PlusIcon className="w-4 h-4" /> Crear Inmueble Manual
        </button>
      </div>

      <AdminPropertyManagement
        propertyCodeAdminInput={propertySearchCode}
        setPropertyCodeAdminInput={setPropertySearchCode}
        onSearchPropertyAdmin={handleSearchProperty}
        isLoadingSearch={isLoadingSearch}
        foundPropertyForAdmin={foundProperty}
        onEditProperty={(prop) => setEditingProperty(prop)}
        message={message}
      />

      {editingProperty && (
        <EditPropertyModal
          property={editingProperty}
          isOpen={!!editingProperty}
          onSave={handleSaveProperty}
          onClose={() => setEditingProperty(null)}
        />
      )}

      {isCreateModalOpen && (
        <CreatePropertyModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSave={handleCreateProperty}
        />
      )}
    </section>
  );
};
